import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Router, RouterModule } from '@angular/router';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { GoogleTagManagerService } from '@app/services/google-tag-manager.service';
import { environment } from '../environments/environment';
import {
  NgcCookieConsentService,
  NgcStatusChangeEvent,
} from 'ngx-cookieconsent';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isPlatformServer } from '@angular/common';

@Component({
  standalone: true,
  imports: [NgxSpinnerModule, RouterModule],
  selector: 'app-root',
  template: `
    <router-outlet />
    <ngx-spinner
      [bdColor]="'rgb(24, 46, 149, 0.75)'"
      [color]="'#fff'"
      [fullScreen]="true"
      [size]="'large'"
      [type]="'ball-scale-multiple'"
    >
      <p style="color: #fff">Ładowanie...</p>
    </ngx-spinner>
  `,
})
export class AppComponent implements OnInit, AfterViewInit {
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _googleTagManagerService = inject(GoogleTagManagerService);
  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _angulartics2GoogleTagManager = inject(
    Angulartics2GoogleTagManager,
  );

  private readonly _ngcCookieConsentService = inject(NgcCookieConsentService);

  public ngOnInit(): void {
    if (isPlatformServer(this._platformId)) {
      return;
    }
    this._ngcCookieConsentService.statusChange$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((event: NgcStatusChangeEvent) => {
        if (!environment.production) {
          console.warn('Consent status changed: ', event.status);
        }
        this._setConsentMode();
      });
  }

  public ngAfterViewInit(): void {
    this._handleGtmScript();
  }

  private _handleGtmScript() {
    if (!environment.production) {
      return;
    }
    this._googleTagManagerService
      .addScriptToDom()
      .then(() => {
        this._angulartics2GoogleTagManager.startTracking();
        this._setConsentMode();
      })
      .catch((error) => {
        console.error(error);
      });
  }

  private _setConsentMode(): void {
    const hasAnswered = this._ngcCookieConsentService.hasAnswered();
    const hasConsented = this._ngcCookieConsentService.hasConsented();
    if (!environment.production) {
      console.warn('Setting consent mode');
      console.warn('Has answered: ', hasAnswered);
      console.warn('Has consented: ', hasConsented);
    }
    if (hasAnswered) {
      if (hasConsented) {
        this._googleTagManagerService.gtag('consent', 'update', {
          ad_storage: 'granted',
          ad_user_data: 'granted',
          ad_personalization: 'granted',
          analytics_storage: 'granted',
        });
      } else {
        this._googleTagManagerService.gtag('consent', 'update', {
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
          analytics_storage: 'denied',
        });
      }
    } else {
      this._googleTagManagerService.gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
      });
    }
  }
}
